import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useVertical } from 'lib/Hooks/useVertical';
import { useBedrockRegistration } from 'store/bedrockRegistration';
import { Description } from 'components/FormFields/Description';
import { SubmitButton } from 'components/FormFields/SubmitButton';
import { RecaptchaForm } from 'components/AccountLoginRegistration/RecaptchaForm';
import { TextField } from 'components/FormFields/TextField';
import { ErrorMsg } from 'components/FormFields/ErrorMsg';
import { getBedrockBrandForVertical } from 'lib/vertical';
import { emailSchema } from '../validationSchema';

/**
 * Screen to enter email to trigger log in with one-time code process
 * @returns {React.ReactElement} The rendered component
 */
const LogInOTCScreen = () => {
  const { vertical } = useVertical();
  const requestOneTimeCode = useBedrockRegistration((state) => state.requestOneTimeCode);
  const setScreen = useBedrockRegistration((state) => state.setScreen);
  const setEmail = useBedrockRegistration((state) => state.setEmail);
  const error = useBedrockRegistration((state) => state.error);

  const methods = useForm({
    resolver: yupResolver(emailSchema),
  });

  /**
   * Handles form submission for requesting a one-time code.
   * @param {object} data - The form data containing the email.
   * @param {string} data.email - The email address to send the one-time code to.
   */
  const onSubmit = (data) => {
    const brand = getBedrockBrandForVertical(vertical);
    const trackingPayload = {
      action: 'SignIn:Start',
      method: 'LogInOTCScreen:SubmitButton:Click',
    };
    requestOneTimeCode({ brand, email: data.email, trackingPayload }).then((result) => {
      if (result) {
        setEmail(data.email);
        setScreen(result);
      }
    });
  };

  return (
    <>
      <h2 data-testid="log-in-otc">Log in with code</h2>
      <ErrorMsg message={error} />
      <Description>
        Get a one-time code sent to your email that will sign you in without your password
      </Description>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <RecaptchaForm
          submitCallback={onSubmit}
        >
          <TextField
            name="email"
            isRequired
            dataTestId="email-input-field"
            type="email"
            placeholder="Email"
          />
          <SubmitButton
            title="Continue"
          />
        </RecaptchaForm>
      </FormProvider>
    </>
  );
};

export { LogInOTCScreen };
